// Bunker Hill metal-accounting seed — Module 2 (word-doc §M2, worked example
// §6). Frozen at the same 08 Jun 2026 story moment as the rest of the seed.
//
// Provenance convention (seed-data rule R1): every literal cites the module
// doc section (A §M2 §n), the deck page (B: <page>), or the corpus register
// (MA<n>); anything with no source is [syn]. MA1/S1: the worked-example
// numbers are A-side by design — the module renders Paul's own document
// reconciliation verbatim; the seed's B-side design column is not rebased.
//
// Consumed by metal-accounting Scene.transform.ts (Module 2 operate surface).
// Corpus map: docs/audits/2026-08-20-metal-accounting-corpus.md.

import type {
  AccountingPeriodReport,
  GrossErrorFlag,
  Measurement,
  MetalInventory,
  MineClaimRow,
  PeriodCloseRow,
  ReconciledValue,
  ReconciliationCheck,
  ShipmentRow,
} from '@/versions/v1/schema/metal-accounting';

/** A §M2 §6.1 + M1 §8.4: the reconciliation-relevant readings. */
export const MEASUREMENTS: readonly Measurement[] = [
  {
    id: 'ms-wit021',
    streamId: 'st-rom',
    source: 'dcs-tag',
    decode: 'metered',
    sourceRef: 'WIT-021',
    rawValue: '1,800 stpd',
    variance:
      'No independent cross-check on fresh feed tonnage — a calibration drift would silently bias every downstream number (A §M2 §6.1)',
    timestamp: '08 Jun 2026 · design-basis stand-in',
    provenance: 'A §M2 §6.1 — "the primary mass balance anchor for the entire concentrator"',
  },
  {
    id: 'ms-fit203',
    streamId: 'st-cyc-uf',
    source: 'dcs-tag',
    decode: 'metered',
    sourceRef: 'FIT-203',
    rawValue: '153 m³/h',
    variance: 'With DT-203 on cyclone underflow — volumetric flow for circulating-load solids cross-check',
    timestamp: '08 Jun 2026 · design-basis stand-in',
    provenance: 'A §M2 §6.1 · [syn] cross-check pair for 186 stph circulating load (vision.V10 #314)',
  },
  {
    id: 'ms-dt203',
    streamId: 'st-cyc-uf',
    source: 'dcs-tag',
    decode: 'metered',
    sourceRef: 'DT-203',
    rawValue: '1.78 t/m³',
    variance: 'Underflow slurry density — with FIT-203 converts to solids stph against design circulating load',
    timestamp: '08 Jun 2026 · design-basis stand-in',
    provenance: 'A §M2 §6.1 · [syn] underflow density for 186 stph cross-check (vision.V10 #314)',
  },
  {
    id: 'ms-sa03',
    streamId: 'st-pbcon',
    source: 'lims-sample',
    decode: 'sampled',
    sourceRef: 'SA03',
    rawValue: '67.1% Pb · 12.14 oz/t Ag',
    variance: '',
    timestamp: '08 Jun 2026 · design-basis stand-in',
    provenance: 'A §M1 §8.4',
    precision: { pbPct: 0.25, agOzPerT: 0.25 },
  },
  {
    id: 'ms-sa04',
    streamId: 'st-zncon',
    source: 'lims-sample',
    decode: 'sampled',
    sourceRef: 'SA04',
    rawValue: '58.4% Zn',
    variance: '',
    timestamp: '08 Jun 2026 · design-basis stand-in',
    provenance: 'A §M1 §8.4',
    precision: { znPct: 0.3 },
  },
  {
    id: 'ms-sa05',
    streamId: 'st-tails',
    source: 'lims-sample',
    decode: 'sampled',
    sourceRef: 'SA05',
    rawValue: '0.18% Pb · 1.08% Zn · 0.082 oz/t Ag',
    variance: '',
    timestamp: '08 Jun 2026 · design-basis stand-in',
    provenance: 'A §M2 §6.4 — tailings metal reads "from SA05 assay"',
    precision: { pbPct: 0.05, znPct: 0.05, agOzPerT: 0.008 },
  },
];

/** Corpus §2.3: raw vs reconciled as distinct, state-carrying values. */
export const RECONCILED_VALUES: readonly ReconciledValue[] = [
  {
    id: 'rv-zn-metal',
    streamId: 'st-zncon',
    parameter: 'Contained Zn in concentrate (daily)',
    measurementId: 'ms-sa04',
    reconciledValue: '83.54 dst Zn/day',
    adjustment: 'Two independent calculation paths agree — recovery basis 83.33 vs mass × grade 83.54',
    closureError: '0.25% — consistent',
    state: 'reconciled',
    provenance: 'A §M2 §6.2 (A-side design numbers; MA1)',
  },
  {
    id: 'rv-pb-metal',
    streamId: 'st-pbcon',
    parameter: 'Contained Pb in concentrate (daily)',
    measurementId: 'ms-sa03',
    reconciledValue: '38.10 dst Pb/day',
    adjustment:
      'Recovery basis 38.10 dst Pb/day; mass × grade 38.10 dst Pb/day — corrected Pb concentrate mass 56.87 dst/day at 67.0% Pb (was 72.26 dst/day)',
    closureError: '0.24% — consistent',
    state: 'reconciled',
    provenance: 'A §M2 §6.2 — MA2 closed: concentrate mass retied to PFS recovery path (vision.V2 #307)',
  },
  {
    id: 'rv-ag-metal',
    streamId: 'st-pbcon',
    parameter: 'Contained Ag in concentrate (daily)',
    measurementId: 'ms-sa03',
    reconciledValue: '689.8 oz Ag/day',
    adjustment:
      'Concentrate route primary: 56.87 dst Pb/day × 12.13 oz/t Ag = 689.8 oz Ag/day; PFS feed-grade table 1.00 oz/t recorded in provenance only (seed.P2 #291)',
    closureError: '0.23% — consistent',
    state: 'reconciled',
    provenance:
      'A §M2 §6.2 — MA3 closed: despatch-assay concentrate route; head Ag implied at 0.455 oz/t (seed.P2 #291)',
  },
  {
    id: 'rv-circload',
    streamId: 'st-cyc-uf',
    parameter: 'Circulating load cross-check',
    measurementId: 'ms-fit203',
    reconciledValue: '186 stph',
    adjustment:
      'FIT-203 153 m³/h × DT-203 1.78 t/m³ → 186.1 stph solids (ρ_solids 3.4 [syn]) vs 186 stph design — within 0.05%',
    closureError: '0.05% — consistent',
    state: 'reconciled',
    provenance: 'A §M2 §6.1 · M1 §8.3 · [syn] cyclone underflow cross-check (vision.V10 #314)',
  },
];

/** A §M2 §6.1 — the one seeded flag; links to the WIT-021 data-quality drawer. */
export const GROSS_ERROR_FLAGS: readonly GrossErrorFlag[] = [
  {
    id: 'gef-wit021',
    measurementId: 'ms-wit021',
    testStatistic: 'Single-anchor exposure — redundancy required for a measurement test, and none exists at this node',
    suspectScore: 'Cannot be error-checked as specified — balanced by difference only',
    suggestedAction:
      'Bring FIT-203 + DT-203 into the reconciliation as genuine redundancy, not just a display tag (A §M2 §7)',
    narrative:
      'As specified today, the entire metal accounting chain hangs off one instrument. There is no independent cross-check on fresh feed tonnage, so a WIT-021 calibration drift would silently bias every downstream recovery and grade number with nothing in the current instrument list positioned to catch it.',
    provenance: 'A §M2 §6.1',
  },
];

/** A §M2 §6.3 — holdup nodes estimated from residence time × flow [syn] (vision.V10 #314). */
export const METAL_INVENTORY: readonly MetalInventory[] = [
  {
    id: 'mi-grind',
    subprocessId: 'sp-grind',
    metal: 'Pb · Zn · Ag',
    status: 'estimated',
    residenceMinutes: 25,
    holdupEstimate: '',
    inventoryChangeDelta: '+0.3 dst Pb · +0.6 dst Zn · +4 oz Ag',
    asOfTimestamp: '08 Jun 2026',
    note: 'Ball mill / cyclone loop — 186 stph circulating load holds metal at any instant',
    provenance: '[syn] residence 25 min × grind-loop solids flow · head grade (vision.V10 #314)',
  },
  {
    id: 'mi-pb-cells',
    subprocessId: 'sp-pb',
    metal: 'Pb · Ag',
    status: 'estimated',
    residenceMinutes: 30,
    holdupEstimate: '',
    inventoryChangeDelta: '+0.2 dst Pb · +3 oz Ag',
    asOfTimestamp: '08 Jun 2026',
    note: 'Pb flotation cell banks — residence 30 min on circuit slurry',
    provenance: '[syn] residence 30 min × Pb circuit holdup flow · Pb rougher conc grade (vision.V10 #314)',
  },
  {
    id: 'mi-zn-cells',
    subprocessId: 'sp-zn',
    metal: 'Zn',
    status: 'estimated',
    residenceMinutes: 40,
    holdupEstimate: '',
    inventoryChangeDelta: '+0.4 dst Zn',
    asOfTimestamp: '08 Jun 2026',
    note: 'Zn flotation cell banks — residence 40 min on circuit slurry',
    provenance: '[syn] residence 40 min × Zn circuit holdup flow · Zn circuit feed grade (vision.V10 #314)',
  },
  {
    id: 'mi-thickeners',
    subprocessId: 'sp-filter',
    metal: 'Pb · Zn · Ag',
    status: 'estimated',
    residenceMinutes: 360,
    holdupEstimate: '',
    inventoryChangeDelta: '+0.1 dst Pb · +0.3 dst Zn · +1 oz Ag',
    asOfTimestamp: '08 Jun 2026',
    note: 'Three thickeners — 6 h residence on combined underflow',
    provenance: '[syn] residence 360 min × thickener underflow · blended concentrate grade (vision.V10 #314)',
  },
];

/** ma.D1 / #120 (MA4 answered), day period only: the mine's own claim for
 *  tonnes to surface and ROM head grade per element — Paul's 14 Aug note ¶57
 *  ("Reconciliation: between mine, mill and concentrate shipped") and the
 *  stockpile disconnect at ¶781. No Bunker Hill source names a mine-side
 *  figure (corpus MA4) — every value here is [syn]. Shaped so Zn and Ag tie
 *  with the mill's WIT-021-anchored feed metal within ~1%, and Pb shows the
 *  mine claiming ~4% more contained Pb than the mill accounts (a mine ↔ mill
 *  gap only — see reconciliation.md § Mine, mill and shipped). */
// H1 #668: D14's measured 1,720 dst/d replaces the former 1,800 design
// mill basis. Rebase only the synthetic day legs, preserving MR-1's Pb-only
// mine gap: mine Pb 1,740 × .0247 = 42.978 vs mill 1,720 × .024 = 41.28
// (+4.113% claimed). K17 round 1 (#663) re-bases the day despatch onto the
// mill concentrate, not feed metal (see SHIPMENTS).
export const MINE_CLAIMS: readonly MineClaimRow[] = [
  {
    id: 'mc-day',
    period: 'day',
    tonnesToSurface: '1,740 stpd',
    grades: [
      { element: 'Zn', grade: '5.40% Zn' },
      { element: 'Pb', grade: '2.47% Pb' },
      { element: 'Ag', grade: '0.44 oz/t Ag' },
    ],
    source: 'Mine survey — block-model estimate, ROM stockpile',
    provenance: '[syn] mine survey claim — no site source',
  },
];

/** ma.D1 / #120, day period only: the concentrate despatch record — tonnes
 *  and grade per product leaving the gate. [syn], no Bunker Hill source
 *  (corpus MA4). K17 round 1 (#663): shipped metal is compared with the mill's
 *  accounted **concentrate** (feed × recovery), not with mill feed metal — a
 *  day cannot ship metal the plant never produced. The two lots sit about 0.3 %
 *  under that produced figure (a small stock move):
 *    Zn     132.40 dst/day × 58.0 % = 76.792 dst vs 77.006 dst produced = −0.28 %
 *    Pb/Ag   52.28 dst/day × 67.0 % = 35.028 dst Pb vs 35.129 dst produced = −0.29 %
 *            52.28 dst/day × 12.12 oz/t = 633.6 oz Ag vs 635.6 oz produced = −0.30 %
 *  Produced = 1,720 dst/d × 5.44 % / 2.40 % / 0.442 oz/st × 82.3 % / 85.1 % /
 *  83.6 % recovery. The demo's flagged Pb gap sits at mine ↔ mill, not here
 *  (see reconciliation.md). */
export const SHIPMENTS: readonly ShipmentRow[] = [
  {
    id: 'sh-zn-day',
    period: 'day',
    product: 'Zn concentrate',
    tonnes: '132.40 dst/day',
    grades: [{ element: 'Zn', grade: '58.0% Zn' }],
    source: 'Concentrate despatch record',
    provenance: '[syn] concentrate despatch — no site source',
  },
  {
    id: 'sh-pbag-day',
    period: 'day',
    product: 'Pb/Ag concentrate',
    tonnes: '52.28 dst/day',
    grades: [
      { element: 'Pb', grade: '67.0% Pb' },
      { element: 'Ag', grade: '12.12 oz/t Ag' },
    ],
    source: 'Concentrate despatch record',
    provenance: '[syn] concentrate despatch — no site source',
  },
  // May 2026 closed month — three Pb/Ag lots, three Zn lots [syn] (emails.A4 #321).
  // K17 (#663): the lots are a seed choice, not a result. The measured May month
  // (D14 / the `rs-*` series) gives a whole-month estimate of Zn 2,432.9 dst,
  // Pb 1,116.0 dst and Ag 18,013 oz, and each new lot sum was set about 0.4 %
  // under it (despatched = estimate × 0.9957 Zn / 0.9960 Pb / 0.9962 Ag), so the
  // close reads under 1 %:
  //   Zn  1,400×58.2% + 1,385×58.4% + 1,375×58.1% = 2,422.5 dst
  //   Pb   564×67.2% +   545×66.8% +   550×67.0% = 1,111.6 dst
  //   Ag   564×10.82 +   545×10.70 +   550×10.93 = 17,945 oz
  // The OLD lots (Zn 1,420/1,398/1,410 × 58.2/58.4/58.1 %; Pb/Ag 620/598/605 ×
  // 67.2/66.8/67.0 % and 12.05/11.92/12.18 oz/t) against that same whole-month
  // estimate read Zn +1.20 %, Pb +9.45 %, Ag +21.95 % — not "within 1 %".
  // The cause was not a stale Ag basis but May's measured Ag head grade:
  // 0.402 oz/st over 25–31 May (against 0.442 now) implies Pb/Ag concentrate Ag
  // of 0.402 × 83.43 / (2.437 × 85.26) × 67 = 10.81 oz/t, which is where the
  // new 10.70–10.93 oz/t lots come from.
  {
    id: 'sh-pb-may-1',
    period: 'mtd',
    closeMonth: 'may-2026',
    despatchDate: '03 May 2026',
    product: 'Pb/Ag concentrate',
    tonnes: '564 dst',
    moisturePct: 9.2,
    grades: [
      { element: 'Pb', grade: '67.2% Pb' },
      { element: 'Ag', grade: '10.82 oz/t Ag' },
    ],
    source: 'Smelter settlement · lot PB-MAY-01',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  {
    id: 'sh-pb-may-2',
    period: 'mtd',
    closeMonth: 'may-2026',
    despatchDate: '12 May 2026',
    product: 'Pb/Ag concentrate',
    tonnes: '545 dst',
    moisturePct: 9.0,
    grades: [
      { element: 'Pb', grade: '66.8% Pb' },
      { element: 'Ag', grade: '10.70 oz/t Ag' },
    ],
    source: 'Smelter settlement · lot PB-MAY-02',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  {
    id: 'sh-pb-may-3',
    period: 'mtd',
    closeMonth: 'may-2026',
    despatchDate: '24 May 2026',
    product: 'Pb/Ag concentrate',
    tonnes: '550 dst',
    moisturePct: 9.1,
    grades: [
      { element: 'Pb', grade: '67.0% Pb' },
      { element: 'Ag', grade: '10.93 oz/t Ag' },
    ],
    source: 'Smelter settlement · lot PB-MAY-03',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  {
    id: 'sh-zn-may-1',
    period: 'mtd',
    closeMonth: 'may-2026',
    despatchDate: '05 May 2026',
    product: 'Zn concentrate',
    tonnes: '1,400 dst',
    moisturePct: 8.8,
    grades: [{ element: 'Zn', grade: '58.2% Zn' }],
    source: 'Smelter settlement · lot ZN-MAY-01',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  {
    id: 'sh-zn-may-2',
    period: 'mtd',
    closeMonth: 'may-2026',
    despatchDate: '15 May 2026',
    product: 'Zn concentrate',
    tonnes: '1,385 dst',
    moisturePct: 8.9,
    grades: [{ element: 'Zn', grade: '58.4% Zn' }],
    source: 'Smelter settlement · lot ZN-MAY-02',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  {
    id: 'sh-zn-may-3',
    period: 'mtd',
    closeMonth: 'may-2026',
    despatchDate: '28 May 2026',
    product: 'Zn concentrate',
    tonnes: '1,375 dst',
    moisturePct: 8.7,
    grades: [{ element: 'Zn', grade: '58.1% Zn' }],
    source: 'Smelter settlement · lot ZN-MAY-03',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  // June 2026 to date — two lots despatched so far [syn].
  // K17 round 2 (#663): the June close closes D25's window (1–7 Jun) unscaled,
  // so the concentrate produced it compares against is the flowsheet's own MTD
  // concentrate metal — feed × recovery over the 7 complete production days
  // (12,401 dst × window-mean head × window-mean recovery): Zn 566.036 dst,
  // Pb 252.698 dst, Ag 4,232.34 oz. Both lots are a seed choice set to the
  // plant's usual batch-shipping stock on hand — despatched = **89 % of
  // concentrate produced**, leaving ~11 % not yet despatched:
  //   Zn   863 × 58.4 % = 503.992 dst vs 566.036 produced = 89.04 % (−62.0 dst, 11.0 %)
  //   Pb   335 × 67.1 % = 224.785 dst vs 252.698 produced = 88.95 % (−27.9 dst, 11.0 %)
  //   Ag   335 × 11.24  = 3,765.4 oz  vs 4,232.34 produced = 88.97 % (−466.9 oz, 11.0 %)
  // Both lots are dated inside the close's window; a lot dated 8 Jun or later
  // falls outside D25's 1–7 Jun window and is not counted by the June-to-date
  // close (`despatchWindowFor` / `despatchInWindow`). */
  {
    id: 'sh-pb-jun-1',
    period: 'mtd',
    closeMonth: 'june-2026-mtd',
    despatchDate: '02 Jun 2026',
    product: 'Pb/Ag concentrate',
    tonnes: '335 dst',
    moisturePct: 9.0,
    grades: [
      { element: 'Pb', grade: '67.1% Pb' },
      { element: 'Ag', grade: '11.24 oz/t Ag' },
    ],
    source: 'Smelter settlement · lot PB-JUN-01',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
  {
    id: 'sh-zn-jun-1',
    period: 'mtd',
    closeMonth: 'june-2026-mtd',
    despatchDate: '04 Jun 2026',
    product: 'Zn concentrate',
    tonnes: '863 dst',
    moisturePct: 8.8,
    grades: [{ element: 'Zn', grade: '58.4% Zn' }],
    source: 'Smelter settlement · lot ZN-JUN-01',
    provenance: '[syn] invoiced concentrate assay — illustrative smelter settlement (emails.A4 #321)',
  },
];

/** Period close metadata — figures computed in `periodClosePanel()` from lots + series. */
export const PERIOD_CLOSE: readonly PeriodCloseRow[] = [
  {
    id: 'pc-zn-may',
    month: 'may-2026',
    element: 'Zn',
    despatchedContained: '',
    estimateContained: '',
    unaccountedLoss: '',
    unaccountedPct: 0,
    state: 'closed',
    competentPerson: 'J. Reid [syn]',
    closedAt: '05 Jun 2026',
    provenance: '[syn] May close vs head × recovery · smelter settlement illustrative (emails.A4 #321)',
  },
  {
    id: 'pc-pb-may',
    month: 'may-2026',
    element: 'Pb',
    despatchedContained: '',
    estimateContained: '',
    unaccountedLoss: '',
    unaccountedPct: 0,
    state: 'closed',
    competentPerson: 'J. Reid [syn]',
    closedAt: '05 Jun 2026',
    provenance: '[syn] May close vs head × recovery · smelter settlement illustrative (emails.A4 #321)',
  },
  {
    id: 'pc-ag-may',
    month: 'may-2026',
    element: 'Ag',
    despatchedContained: '',
    estimateContained: '',
    unaccountedLoss: '',
    unaccountedPct: 0,
    state: 'closed',
    competentPerson: 'J. Reid [syn]',
    closedAt: '05 Jun 2026',
    provenance: '[syn] May close vs head × recovery · smelter settlement illustrative (emails.A4 #321)',
  },
  {
    id: 'pc-zn-jun',
    month: 'june-2026-mtd',
    element: 'Zn',
    despatchedContained: '',
    estimateContained: '',
    unaccountedLoss: '',
    unaccountedPct: 0,
    state: 'open',
    provenance: '[syn] June to date vs head × recovery over 1–7 Jun · lots at 89 % of produced (emails.A4 #321)',
  },
  {
    id: 'pc-pb-jun',
    month: 'june-2026-mtd',
    element: 'Pb',
    despatchedContained: '',
    estimateContained: '',
    unaccountedLoss: '',
    unaccountedPct: 0,
    state: 'open',
    provenance: '[syn] June to date vs head × recovery over 1–7 Jun · lots at 89 % of produced (emails.A4 #321)',
  },
  {
    id: 'pc-ag-jun',
    month: 'june-2026-mtd',
    element: 'Ag',
    despatchedContained: '',
    estimateContained: '',
    unaccountedLoss: '',
    unaccountedPct: 0,
    state: 'open',
    provenance: '[syn] June to date vs head × recovery over 1–7 Jun · lots at 89 % of produced (emails.A4 #321)',
  },
];

/** A §M2 §6.2 — the check-in/check-out exercise, transcribed verbatim. */
export const RECONCILIATION_CHECK: ReconciliationCheck = {
  id: 'rc-design-basis',
  title: 'Check-in / check-out: cross-checking the two source documents',
  description:
    'Same fresh feed (1,800 stpd) and design feed grades feed two independent calculation paths for daily contained metal in each final concentrate — one from the PFS recovery targets in the Functional Description, one from the concentrate tonnage and grade in the Mass Balance workbook. In a fully consistent design basis these should agree.',
  columns: [
    { id: 'zn', name: 'Zn circuit', verdict: 'consistent' },
    { id: 'pb', name: 'Pb circuit', verdict: 'consistent' },
    { id: 'ag', name: 'Ag in Pb/Ag circuit', verdict: 'consistent' },
  ],
  rows: [
    { id: 'rcr-feed-grade', label: 'Feed grade (PFS design)', values: ['5.44% Zn', '2.40% Pb', '0.455 oz/ton Ag'] },
    { id: 'rcr-metal-in', label: 'Metal in fresh feed (1,800 stpd × grade)', values: ['97.92 dst Zn/day', '43.20 dst Pb/day', '819.0 oz Ag/day'] },
    { id: 'rcr-design-rec', label: 'Design recovery (PFS)', values: ['85.1%', '88.2%', '84.2%'] },
    { id: 'rcr-rec-basis', label: 'Metal recovered — recovery basis', values: ['83.33 dst Zn/day', '38.10 dst Pb/day', '689.8 oz Ag/day'] },
    { id: 'rcr-conc-mass', label: 'Final concentrate mass (Mass Balance workbook)', values: ['144.03 dst/day', '57.0 dst/day', '57.0 dst/day'] },
    { id: 'rcr-conc-grade', label: 'Concentrate grade (PFS design)', values: ['58.0% Zn', '67.0% Pb', '12.13 oz/ton Ag'] },
    { id: 'rcr-mass-basis', label: 'Metal in concentrate — mass × grade basis', values: ['83.54 dst Zn/day', '38.19 dst Pb/day', '691.4 oz Ag/day'] },
    { id: 'rcr-gap', label: 'Gap between the two calculation paths', values: ['0.25% — consistent', '0.24% — consistent', '0.23% — consistent'] },
    { id: 'rcr-implied', label: 'Implied recovery from Mass Balance workbook numbers', values: ['85.1% (matches design)', '88.2% (matches design)', '84.2% (matches design)'] },
  ],
  narrative:
    'All three circuits tie out to within rounding: Zn 0.25%, Pb 0.24% and Ag 0.23% between the recovery-basis and mass × grade paths.',
  provenance: 'A §M2 §6.2 — MA1: A-side design numbers, rendered as the document story they are',
};

/** ma.D1 / #120 fix round 1 (MR-F5): the Ag design feed grade, same value and
 *  provenance as the Metallurgist landing's `met-ag-feed` KPI tile (B: slide
 *  06), duplicated here so the mill checkpoint reads from the accounting seed
 *  rather than a landing-page KPI array owned by another stream. Zn/Pb design
 *  feed grades already live in `RECONCILIATION_CHECK` (`rcr-feed-grade`); Ag
 *  has no Zn/Pb-style two-path check row, so it gets its own constant with the
 *  same provenance grammar. */
export const AG_FEED_GRADE_DESIGN = {
  grade: '0.455 oz/t Ag',
  provenance: 'PFS 2022 feed-grade table says 1.00 oz/t — mass-balance-consistent figure from concentrate route (seed.P2 #291)',
};

/** A §M2 §6.4 — the P754-shaped daily report, design-basis stand-in. */
export const ACCOUNTING_REPORT: AccountingPeriodReport = {
  id: 'apr-daily',
  period: 'day',
  periodLabel: 'Daily · design-basis stand-in for the day’s reconciled actuals',
  circuits: ['Pb/Ag circuit', 'Zn circuit'],
  lines: [
    {
      id: 'al-feed',
      label: 'Feed',
      cells: [
        { text: '1,800 stpd @ 2.40% Pb', state: 'value' },
        { text: 'as above @ 5.44% Zn', state: 'value' },
      ],
    },
    {
      id: 'al-metal-in',
      label: 'Metal in feed',
      cells: [
        { text: '43.20 dst Pb', state: 'value' },
        { text: '97.92 dst Zn', state: 'value' },
      ],
    },
    {
      id: 'al-metal-conc',
      label: 'Metal in concentrate (reconciled)',
      cells: [
        { text: '38.10 dst Pb', state: 'value' },
        { text: '83.54 dst Zn', state: 'value' },
      ],
    },
    {
      id: 'al-metal-tails',
      label: 'Metal in tailings',
      cells: [
        { text: 'from SA05 assay', state: 'value' },
        { text: 'from SA05 assay', state: 'value' },
      ],
    },
    {
      id: 'al-inventory',
      label: 'Inventory change (mill, cells, thickeners)',
      cells: [
        { text: '+0.6 dst Pb', state: 'value' },
        { text: '+1.3 dst Zn', state: 'value' },
      ],
    },
    {
      id: 'al-unaccounted',
      label: 'Unaccounted (should trend to ~0)',
      cells: [
        { text: '−0.4 dst Pb (−0.9%)', state: 'within-tolerance' },
        { text: '−0.8 dst Zn (−0.9%)', state: 'within-tolerance' },
      ],
    },
  ],
  provenance: 'A §M2 §6.4',
};
